import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { HStack, ProductCardV2, theme, useReduceMotionStatus } from '@sg/garnish';

/**
 * Favorites placeholders shaped like the favorite cards, laid out in the same
 * grid, so the page keeps its shape when the favorites arrive.
 */
export const AccountFavoritesLoading = (props: Props) => {
  const { itemsPerRow } = props;

  const placeholderIndexes = [...Array(itemsPerRow * PLACEHOLDER_ROWS).keys()];

  return (
    <HStack itemsPerRow={itemsPerRow} style={styles.stack}>
      {placeholderIndexes.map((index) => (
        <FavoriteCardPlaceholder key={index} index={index} />
      ))}
    </HStack>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const FavoriteCardPlaceholder = (props: FavoriteCardPlaceholderProps) => {
  const { index } = props;

  const reduceMotionStatus = useReduceMotionStatus();

  const pulseOpacity = useSharedValue(1);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (reduceMotionStatus !== 'disabled') {
      return;
    }

    // Each card starts a little later, so the pulse ripples through the grid.
    pulseOpacity.value = withDelay(
      index * PULSE_STAGGER,
      withRepeat(withTiming(PULSE_MIN_OPACITY, PULSE_TIMING_CONFIG), -1, true),
    );
  }, [index, pulseOpacity, reduceMotionStatus]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const pulseStyle = useAnimatedStyle(() => ({ opacity: pulseOpacity.value }));
  const contentStyles = [styles.content, pulseStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ProductCardV2.Container canHover={false}>
      <Animated.View style={contentStyles}>
        <View style={styles.image} />

        <View style={styles.labelRow}>
          <View style={styles.label} />
        </View>

        <View style={styles.heading} />

        <View style={styles.description}>
          <View style={styles.descriptionLineFull} />

          <View style={styles.descriptionLineLong} />

          <View style={styles.descriptionLineShort} />
        </View>

        <View style={styles.footer}>
          <View style={styles.calories} />

          <View style={styles.buttons}>
            <View style={styles.addToBagButton} />

            <View style={styles.shareIcon} />
          </View>
        </View>
      </Animated.View>
    </ProductCardV2.Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_ROWS = 2;

const PULSE_MIN_OPACITY = 0.5;
const PULSE_STAGGER = 120;
const PULSE_TIMING_CONFIG = { duration: 900, easing: Easing.inOut(Easing.sin) };

// Sizes mirror the favorite card: a 343px image, then text lines centered in their line boxes.
const IMAGE_HEIGHT = 343;

const textPlaceholder = {
  borderRadius: theme.radius.small,
  backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
};

const descriptionLine = { ...textPlaceholder, height: 14, marginVertical: 4 };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stack: {
    marginTop: theme.spacing['4'],
  },
  content: {
    flexGrow: 1,
    rowGap: theme.spacing['1'],
  },
  image: {
    height: IMAGE_HEIGHT,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  labelRow: {
    minHeight: 22,
    justifyContent: 'flex-end',
  },
  label: {
    ...textPlaceholder,
    width: 88,
    height: 12,
    marginBottom: 3,
  },
  heading: {
    ...textPlaceholder,
    width: '64%',
    height: 20,
    marginVertical: 4,
  },
  description: {
    marginBottom: theme.spacing['1'],
  },
  descriptionLineFull: {
    ...descriptionLine,
    width: '100%',
  },
  descriptionLineLong: {
    ...descriptionLine,
    width: '88%',
  },
  descriptionLineShort: {
    ...descriptionLine,
    width: '52%',
    marginTop: theme.spacing['2'] + 4,
  },
  footer: {
    marginTop: 'auto',
  },
  calories: {
    ...textPlaceholder,
    width: 64,
    height: 12,
    marginTop: theme.spacing['5'] + 3,
    marginBottom: 3,
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing['9'],
  },
  addToBagButton: {
    width: 127,
    height: 48,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.SPINACH.NEAR_LIGHTEST,
  },
  shareIcon: {
    width: 24,
    height: 24,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  itemsPerRow: number;
};

type FavoriteCardPlaceholderProps = {
  index: number;
};
